import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Download, CheckCircle2, FileSpreadsheet, Package, Mail } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A' };
const fmt$ = (v) => `$${Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

function ClosePackCard({ api, month, label }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState('');

  const download = async () => {
    setBusy('dl');
    try {
      const res = await api.get(`/finance/close-pack?month=${month}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = `fraime_close_pack_${month}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Close pack for ${label} downloaded`);
    } catch { toast.error('Pack download failed'); }
    setBusy('');
  };

  const send = async () => {
    setBusy('email');
    try {
      const { data } = await api.post('/finance/close-pack/email', { month, to: email });
      if (data.status === 'sent') toast.success(`Close pack emailed to ${email}`, { description: `Xero journal · bordereaux · trust reconciliation — ${label}` });
      else toast.error(data.error || 'Email not sent');
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Email failed'); }
    setBusy('');
  };

  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(26,76,124,0.3)', borderLeft: '4px solid #1A4C7C' }} data-testid="close-pack-card">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4" style={{ color: C.blue }} />
        <Overline>Month-end close pack — {label}</Overline>
      </div>
      <p className="text-xs mt-2" style={{ color: C.slate }}>
        One bundle for the accountant: <span style={{ color: C.navy }}>Xero journal CSV · premium bordereau · claims bordereau · trust reconciliation · summary</span>
      </p>
      <div className="flex items-center gap-3 flex-wrap mt-4">
        <Button onClick={download} disabled={!!busy} variant="outline" className="rounded-sm text-xs" data-testid="close-pack-download">
          <Download className="w-3.5 h-3.5 mr-1.5" />{busy === 'dl' ? 'Bundling…' : 'Download Pack (.zip)'}
        </Button>
        <div className="flex items-center gap-2">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="accountant@firm.com.au"
            className="text-xs px-3 py-2 rounded-sm bg-white outline-none w-56 focus:border-[#1A4C7C]"
            style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }} data-testid="close-pack-email-input" />
          <Button onClick={send} disabled={!!busy || !email.includes('@')} className="rounded-sm text-white text-xs" style={{ background: C.blue }} data-testid="close-pack-email-btn">
            <Mail className="w-3.5 h-3.5 mr-1.5" />{busy === 'email' ? 'Sending…' : 'Email to Accountant'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default function XeroExportPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [downloading, setDownloading] = useState(false);

  const load = useCallback((m) => {
    api.get(`/finance/xero-export/preview${m ? `?month=${m}` : ''}`)
      .then(r => { setData(r.data); setMonth(r.data.month); })
      .catch(() => {});
  }, [api]);

  useEffect(() => { load(''); }, [load]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/finance/xero-export?month=${month}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `xero_manual_journal_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Xero journal for ${data.label} downloaded — import via Accounting → Manual Journals → Import`);
    } catch { toast.error('Export failed'); }
    setDownloading(false);
  };

  if (!data) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>;

  return (
    <div className="space-y-5" data-testid="xero-export-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Xero Journal Export</h2>
          <p className="text-sm mt-0.5" style={{ color: C.slate }}>One click turns the month's premium receipts and claims payments into a Xero-ready Manual Journal CSV — debits and credits balanced, trust money ring-fenced.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={month} onChange={e => load(e.target.value)} data-testid="xero-month-select"
            className="text-sm px-3 py-2 rounded-sm bg-white outline-none"
            style={{ border: '1px solid rgba(136,150,168,0.3)', color: C.navy }}>
            {data.months_available.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <Button onClick={download} disabled={downloading || data.lines.length === 0} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="xero-download-btn">
            <Download className="w-4 h-4 mr-1.5" />{downloading ? 'Preparing…' : 'Download Xero CSV'}
          </Button>
        </div>
      </div>

      <ClosePackCard api={api} month={month} label={data.label} />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
          <Overline>Journals in {data.label}</Overline>
          <p className="text-3xl font-light tracking-tight mt-1" style={{ color: C.navy }}>{data.journals.length}</p>
          <p className="text-[10px] mt-1" style={{ color: C.slate }}>{data.lines.length} journal lines</p>
        </Card>
        <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
          <Overline>Balance check</Overline>
          <p className="text-lg font-semibold mt-2 flex items-center gap-1.5" style={{ color: data.balanced ? C.green : C.red }} data-testid="xero-balance-check">
            <CheckCircle2 className="w-4 h-4" /> {data.balanced ? 'Debits = Credits' : 'OUT OF BALANCE'}
          </p>
          <p className="text-[10px] mt-1" style={{ color: C.slate }}>Every journal nets to zero — Xero will accept the import</p>
        </Card>
        <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
          <Overline>Format</Overline>
          <p className="text-sm font-medium mt-2 flex items-center gap-1.5" style={{ color: C.navy }}>
            <FileSpreadsheet className="w-4 h-4" style={{ color: C.teal }} /> Manual Journal CSV
          </p>
          <p className="text-[10px] mt-1" style={{ color: C.slate }}>Tax rate {data.tax_rate} — GST carried as its own line</p>
        </Card>
      </div>

      <Card className="rounded-sm bg-white overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="xero-preview-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider border-b" style={{ color: C.slate, borderColor: 'rgba(136,150,168,0.15)' }}>
              <th className="text-left py-2.5 px-4 font-semibold">Account</th>
              <th className="text-left py-2.5 px-3 font-semibold">Description</th>
              <th className="text-right py-2.5 px-3 font-semibold">Debit</th>
              <th className="text-right py-2.5 px-4 font-semibold">Credit</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.length === 0 && (
              <tr><td colSpan={4} className="py-8 text-center text-sm" style={{ color: C.slate }}>No paid invoices or settled claims in this month.</td></tr>
            )}
            {data.journals.map(j => {
              const jl = data.lines.filter(l => l.narration === j.narration);
              return (
                <React.Fragment key={j.narration}>
                  <tr style={{ background: '#F5F7FA' }}>
                    <td colSpan={4} className="py-2 px-4 text-xs font-semibold" style={{ color: C.navy }}>
                      {j.narration} <span className="font-normal" style={{ color: C.slate }}>· {j.entries.toLocaleString()} source records · {data.journal_date}</span>
                    </td>
                  </tr>
                  {jl.map((l, i) => (
                    <tr key={i} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                      <td className="py-2 px-4"><span className="font-mono text-xs" style={{ color: C.blue }}>{l.account_code}</span> <span className="text-xs" style={{ color: C.navy }}>{l.account_name}</span></td>
                      <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{l.description}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs" style={{ color: C.navy }}>{l.amount > 0 ? fmt$(l.amount) : ''}</td>
                      <td className="py-2 px-4 text-right font-mono text-xs" style={{ color: C.slate }}>{l.amount < 0 ? fmt$(l.amount) : ''}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>
      <p className="text-[11px]" style={{ color: C.slate }}>In Xero: Accounting → Manual Journals → Import, map the columns and post. Account codes are frAIme defaults — rename them in Xero's chart of accounts if yours differ.</p>
    </div>
  );
}
